import React from "react";
import { AlertTriangle, RefreshCw, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Route-level error boundary.
 *
 * Without it, any render-time exception (bad API payload, missing field) tears
 * down the whole React tree and the user gets a blank white page with no way
 * out. This keeps the shell alive and offers a reset that re-mounts the
 * subtree without a full reload.
 */
export class AppErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the stack in the console so it can be reported/triaged.
    console.error("Route crashed:", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-md rounded-2xl border bg-card shadow-raised p-8 text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-7 w-7 text-destructive" aria-hidden="true" />
          </div>

          <h1 className="text-xl font-semibold text-foreground mb-2">
            Something went wrong
          </h1>
          <p className="text-sm text-muted-foreground mb-5">
            This part of the app hit an unexpected error. Your data is safe —
            try again, or reload if the problem persists.
          </p>

          {this.state.error && (
            <p className="mb-6 rounded-lg bg-muted px-3 py-2 text-left font-mono text-xs text-muted-foreground break-words">
              {this.state.error.message}
            </p>
          )}

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button onClick={this.handleReset} className="gap-2">
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
              Try again
            </Button>
            <Button variant="outline" onClick={this.handleReload} className="gap-2">
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Reload page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;